import { useState, useRef, useCallback } from 'react';

interface UseVoiceRecorderOptions {
  onTranscriptionComplete?: (result: TranscriptionResult) => void;
  onError?: (error: string) => void;
}

interface TranscriptionResult {
  text: string;
  duration?: number;
  structured?: {
    clima?: string;
    equipe?: number;
    atividades?: string;
    tags?: string[];
    ocorrencia_critica?: boolean;
    resumo_cliente?: string;
  };
}

export function useVoiceRecorder(options: UseVoiceRecorderOptions = {}) {
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<number | null>(null);

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        }
      });

      // Check for supported MIME types
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : MediaRecorder.isTypeSupported('audio/webm')
        ? 'audio/webm'
        : 'audio/mp4';

      const mediaRecorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mimeType });
        setAudioBlob(blob);
        
        // Stop all tracks
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorder.start(1000); // Collect data every second
      setIsRecording(true);
      setRecordingTime(0);
      setAudioBlob(null);

      // Start timer
      timerRef.current = window.setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);

    } catch (error) {
      console.error('Error starting recording:', error);
      options.onError?.('Não foi possível acessar o microfone. Verifique as permissões.');
    }
  }, [options]);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
  }, [isRecording]);

  const transcribeAudio = useCallback(async (): Promise<TranscriptionResult | null> => {
    if (!audioBlob) {
      options.onError?.('Nenhum áudio gravado para transcrever.');
      return null;
    }

    setIsTranscribing(true);

    try {
      // Convert blob to base64
      const reader = new FileReader();
      const base64Promise = new Promise<string>((resolve, reject) => {
        reader.onload = () => {
          const result = reader.result as string;
          // Remove the data URL prefix
          const base64 = result.split(',')[1];
          resolve(base64);
        };
        reader.onerror = reject;
      });
      reader.readAsDataURL(audioBlob);
      
      const base64Audio = await base64Promise;

      // Get session for auth
      const { data: { session } } = await import('@/integrations/supabase/client')
        .then(m => m.supabase.auth.getSession());

      if (!session) {
        throw new Error('Usuário não autenticado');
      }

      // Call transcription API
      const response = await fetch(
        `https://hwlswufemzbxdylrtaxv.supabase.co/functions/v1/ai-audio-transcribe`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            audio: base64Audio,
            mimeType: audioBlob.type,
          }),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Erro na transcrição');
      }

      const result: TranscriptionResult = await response.json();
      
      options.onTranscriptionComplete?.(result);
      return result;

    } catch (error) {
      console.error('Transcription error:', error);
      options.onError?.(error instanceof Error ? error.message : 'Erro ao transcrever áudio');
      return null;
    } finally {
      setIsTranscribing(false);
    }
  }, [audioBlob, options]);

  const clearRecording = useCallback(() => {
    setAudioBlob(null);
    setRecordingTime(0);
    chunksRef.current = [];
  }, []);

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  return {
    isRecording,
    isTranscribing,
    recordingTime,
    formattedTime: formatTime(recordingTime),
    audioBlob,
    hasRecording: !!audioBlob,
    startRecording,
    stopRecording,
    transcribeAudio,
    clearRecording,
  };
}
